import {track} from '@core/utils/analytics'

// --------------------------------------------------------------------------
// compressImage — three-tier fallback for iOS Safari compatibility
//
// Tier 1 (fast path):  OffscreenCanvas  — requires iOS 16.4+
// Tier 2 (fallback):   <canvas> + toBlob — works back to Safari 3 / iOS 3
// Tier 3 (pass-through): skip compression entirely — the server runs sharp
//   on every upload (imageResize.server.ts) so an uncompressed file is a
//   bandwidth cost, not a correctness break.
//
// The outer catch fires the upload_compress_failed GA event so we can
// diagnose old-browser failures from GA without needing Web Inspector access.
// --------------------------------------------------------------------------

type OutputType = 'image/jpeg' | 'image/png'

const EXTENSION: Record<OutputType, string> = {'image/jpeg': '.jpg', 'image/png': '.png'}

async function compressViaOffscreenCanvas(
    file: File,
    quality: number,
    maxDim: number,
    type: OutputType = 'image/jpeg',
): Promise<File> {
    const bitmap = await createImageBitmap(file, {imageOrientation: 'from-image'})
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height))
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)
    const canvas = new OffscreenCanvas(width, height)
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()
    const blob = await canvas.convertToBlob({type, quality})
    return new File([blob], file.name.replace(/\.[^.]+$/, EXTENSION[type]), {type})
}

async function compressViaCanvas2d(
    file: File,
    quality: number,
    maxDim: number,
    type: OutputType = 'image/jpeg',
): Promise<File> {
    const url = URL.createObjectURL(file)
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image()
            el.onload = () => resolve(el)
            el.onerror = reject
            el.src = url
        })
        const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight))
        const width = Math.round(img.naturalWidth * scale)
        const height = Math.round(img.naturalHeight * scale)
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(img, 0, 0, width, height)
        return await new Promise<File>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (!blob) { reject(new Error('canvas.toBlob returned null')); return }
                    resolve(new File([blob], file.name.replace(/\.[^.]+$/, EXTENSION[type]), {type}))
                },
                type,
                quality,
            )
        })
    } finally {
        URL.revokeObjectURL(url)
    }
}

// --------------------------------------------------------------------------
// downscaleIfOversized — conditional wrapper around compressImage
//
// Mask-based tools (remove-object, watermark-remover) use inputPolicy: 'preserve'
// deliberately: the file sent to the service should be untouched when it's already
// within bounds, since compressImage always re-encodes to JPEG at `quality` even
// when no resize is needed. This probes the file's natural dimensions first and
// only invokes compressImage when the long edge actually exceeds maxDim — the
// common case (an already-small photo) passes through byte-identical.
// --------------------------------------------------------------------------

async function getImageDimensions(file: File): Promise<{w: number; h: number}> {
    const url = URL.createObjectURL(file)
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve({w: img.naturalWidth, h: img.naturalHeight})
            img.onerror = () => reject(new Error('failed to read image dimensions'))
            img.src = url
        })
    } finally {
        URL.revokeObjectURL(url)
    }
}

export async function downscaleIfOversized(file: File, maxDim: number, quality = 0.92): Promise<File> {
    try {
        const {w, h} = await getImageDimensions(file)
        if (Math.max(w, h) <= maxDim) return file
        return await compressImage(file, quality, maxDim)
    } catch {
        // Dimension probe failed (corrupt file, unsupported format) — fall through with
        // the original file. The server-side pixel cap remains the backstop.
        return file
    }
}

export async function compressImage(file: File, quality = 0.8, maxDim = 1600): Promise<File> {
    // Tier 1: OffscreenCanvas (iOS 16.4+)
    if (typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap === 'function') {
        try {
            return await compressViaOffscreenCanvas(file, quality, maxDim)
        } catch {
            // Fall through to tier 2
        }
    }

    // Tier 2: regular <canvas> + toBlob (iOS 3+, Safari 3+)
    if (typeof document !== 'undefined') {
        try {
            return await compressViaCanvas2d(file, quality, maxDim)
        } catch (err) {
            // Fall through to tier 3; fire diagnostic before giving up
            track('upload_compress_failed', {
                stage: 'canvas_2d',
                error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
                has_offscreen_canvas: typeof OffscreenCanvas !== 'undefined',
                has_create_image_bitmap: typeof createImageBitmap === 'function',
                user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 200),
            })
        }
    } else {
        // SSR context or both tiers unavailable — fire diagnostic
        track('upload_compress_failed', {
            stage: typeof OffscreenCanvas === 'undefined' ? 'offscreen_canvas' : 'image_bitmap',
            error: 'no_canvas_api',
            has_offscreen_canvas: typeof OffscreenCanvas !== 'undefined',
            has_create_image_bitmap: typeof createImageBitmap === 'function',
            user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 200),
        })
    }

    // Tier 3: pass through — server will resize/recompress via sharp
    return file
}

/**
 * Re-encode a picture at its own size to PNG (or JPEG) through the same two canvas tiers — what the
 * clipboard accepts for an image write, where the wall's takes are webp. Unlike `compressImage` there
 * is no pass-through tier: a blob the browser cannot decode or encode rejects, and the caller says so.
 */
export async function reencodeImage(blob: Blob, type: OutputType = 'image/png'): Promise<Blob> {
    const file = new File([blob], 'image', {type: blob.type})
    const keep = Number.MAX_SAFE_INTEGER
    if (typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap === 'function') {
        try {
            return await compressViaOffscreenCanvas(file, 1, keep, type)
        } catch {
            // Fall through to the <canvas> tier
        }
    }
    return compressViaCanvas2d(file, 1, keep, type)
}
